import { invalidateScopedPolicyCatalog, loadScopedPolicies, type TrainingPolicyView } from "@/api";
import {
  DataTableSource,
  type DataTablePageReq,
  type DataTablePageResp,
  type DataTableSortState,
} from "@carbon6/c6-frontend-design";
import { sortSignature } from "@/components/table/sortSignature";
import {
  ALL_SESSION_FILTER,
  MAX_POLICY_SELECTION,
  type PolicySelectionReader,
  type SelectedPolicyRef,
  type SessionScope,
} from "@/modules/training/public";
import { policyTextFilterSignature, type PolicyTextFilters } from "./policyFilterFields";
import {
  ALL_POLICY_REGISTERED_WITHIN,
  registeredWithinCutoffMs,
  type PolicyRegisteredWithin,
} from "./policyRegisteredWithin";
import { PolicyTableRow } from "./PolicyTableRow";

type PolicyTableSourceOptions = {
  scope: SessionScope;
  pageSize: number;
  resolveErrorMessage: (error: unknown) => string;
  // 선택 변경 알림. Evaluation 페이지는 이 알림으로 실행 모달을 미리 준비한다.
  onSelectionChange?: (rows: readonly PolicyTableRow[]) => void;
  // 행 클릭 활성화. Evaluation 페이지는 여기서 정책을 선택하고 결과 창을 연다. 이 값이 없으면
  // 행 클릭은 아무 일도 하지 않는다(읽기 전용 호출부).
  onRowActivate?: (row: PolicyTableRow) => void;
  // 첫 로드부터 적용하는 딥링크 필터. initialize() 는 필터된 카탈로그를 바로 조회한다. 필터 없이
  // 조회한 뒤 다시 조회하지 않는다(rules §7).
  initialMemberUuid?: string;
  initialExperimentId?: string;
  initialTextFilters?: PolicyTextFilters;
  initialRegisteredWithin?: PolicyRegisteredWithin;
  // URL 이 준 선택(?policy=<id> 반복). 첫 페이지가 해석되면 전체 카탈로그에 한 번 적용한다.
  // 카탈로그에 없는 id 는 알리지 않고 버린다.
  initialSelectedPolicyIds?: readonly string[];
};

// 스코프의 전체 필터 상태. 패널이 URL 에서 만들고 한 번에 적용한다.
export type PolicyQuery = {
  groupUuid: string | null;
  memberUuid: string;
  experimentId: string;
  // 클라이언트 쪽 검색 축. 카탈로그 endpoint 에는 자유 텍스트 필터가 없고, 스코프 전체를
  // 반환한다. 그래서 이 축은 요청을 다시 보내지 않고 캐시된 결과를 좁힌다.
  textFilters: PolicyTextFilters;
  // 등록일 기간 퀵 필터. 텍스트 축과 같이 캐시된 카탈로그를 클라이언트에서 좁힌다.
  registeredWithin: PolicyRegisteredWithin;
};

// My 와 Team 스코프가 함께 쓰는 정책 카탈로그 표 source.
//
// 소유하는 것:
// - 스코프·멤버·experiment 필터로 정책을 조회하고 캐시한다.
// - backend 가 스코프 전체 목록을 반환하므로 클라이언트에서 페이지를 자른다.
//
// 소유하지 않는 것:
// - 멤버와 experiment 드롭다운 선택지. 패널이 따로 불러온다.
export class PolicyTableSource
  extends DataTableSource<PolicyTableRow>
  implements PolicySelectionReader
{
  private readonly scope: SessionScope;
  // Team Training 위쪽에서 고른 팀 그룹. My 스코프는 항상 null 을 쓴다.
  private groupUuid: string | null = null;
  // 초기값은 true 다. 그래야 페이지가 그룹 목록 로딩 여부를 알리기 전에 team 스코프 마운트가 빈
  // 페이지를 확정하지 않는다. My 스코프는 이 값을 보지 않는다.
  private groupResolving = true;
  // 그룹 목록을 받는 동안 열어 둔 페이지의 resolver.
  private heldPage: (() => void) | null = null;
  private memberUuid: string;
  private experimentId: string;
  private textFilters: PolicyTextFilters;
  // 필터와 함께 보관한다. setQuery 의 동등 비교와 cacheSignature 가 같은 직렬화 값을 쓴다.
  private textFilterSignature: string;
  private registeredWithin: PolicyRegisteredWithin;
  private readonly onRowActivate: ((row: PolicyTableRow) => void) | null;
  // 카탈로그를 기다리는 URL 선택 seed. null 이 아니면 선택이 아직 복원되지 않았다. 그동안 URL
  // 미러가 아직 빈 선택으로 파라미터를 덮으면 안 된다.
  private pendingSelectionIds: readonly string[] | null;
  // onPage 가 마지막으로 커밋한 전체 카탈로그의 id→name. 텍스트 필터 전 값이고 real-only 는 뺀다.
  // null 은 카탈로그를 아직 한 번도 로드하지 않았다는 뜻이다.
  private catalogNamesById: ReadonlyMap<string, string> | null = null;

  constructor({
    scope,
    pageSize,
    resolveErrorMessage,
    onSelectionChange,
    onRowActivate,
    initialMemberUuid = ALL_SESSION_FILTER,
    initialExperimentId = ALL_SESSION_FILTER,
    initialTextFilters = [],
    initialRegisteredWithin = ALL_POLICY_REGISTERED_WITHIN,
    initialSelectedPolicyIds,
  }: PolicyTableSourceOptions) {
    super({
      initialPageSize: pageSize,
      resolveErrorMessage,
      onSelectionChange,
      prefetchMode: "paging",
    });
    this.scope = scope;
    this.onRowActivate = onRowActivate ?? null;
    this.memberUuid = initialMemberUuid;
    this.experimentId = initialExperimentId;
    this.textFilters = initialTextFilters;
    this.textFilterSignature = policyTextFilterSignature(initialTextFilters);
    this.registeredWithin = initialRegisteredWithin;
    this.pendingSelectionIds =
      initialSelectedPolicyIds !== undefined && initialSelectedPolicyIds.length > 0
        ? initialSelectedPolicyIds
        : null;
  }

  // URL 선택 seed 를 해석했는지(적용 또는 폐기) 알려 준다. 페이지의 URL 미러는 이 값을 확인한다.
  // 그래서 카탈로그 로드가 느려도 ?policy= 파라미터가 지워지지 않는다.
  isSelectionHydrated(): boolean {
    return this.pendingSelectionIds === null;
  }

  // URL 에서 만든 필터 상태를 한 번에 적용하고 1페이지로 돌아간다. 패널은 쿼리가 서로 맞도록
  // 보장한다. 예: 그룹 변경은 필터가 이미 지워진 상태로 온다. 그래서 한 번 호출하면 다시 로드는
  // 최대 한 번이다.
  setQuery({
    groupUuid,
    memberUuid,
    experimentId,
    textFilters,
    registeredWithin,
  }: PolicyQuery): Promise<void> {
    const textFilterSignature = policyTextFilterSignature(textFilters);
    if (
      groupUuid === this.groupUuid &&
      memberUuid === this.memberUuid &&
      experimentId === this.experimentId &&
      textFilterSignature === this.textFilterSignature &&
      registeredWithin === this.registeredWithin
    ) {
      return Promise.resolve();
    }
    // 그룹이나 멤버가 다르면 정책 집합이 다르다. 그래서 선택을 유지할 수 없다. experiment·검색·
    // 등록 기간은 같은 집합을 좁히기만 하므로 선택을 유지한다.
    if (groupUuid !== this.groupUuid || memberUuid !== this.memberUuid) {
      this.clearSelection();
      // 대기 중인 URL seed 는 딥링크의 첫 null → group 해석을 거쳐도 남는다. 실제 그룹 *전환*은
      // 현재 선택과 함께 seed 도 무효로 만든다.
      if (this.groupUuid !== null || memberUuid !== this.memberUuid) {
        this.pendingSelectionIds = null;
      }
    }
    this.groupUuid = groupUuid;
    this.memberUuid = memberUuid;
    this.experimentId = experimentId;
    this.textFilters = textFilters;
    this.textFilterSignature = textFilterSignature;
    this.registeredWithin = registeredWithin;
    // 열어 둔 페이지는 이전 쿼리 상태의 것이다. 남는 것이 없도록 그 페이지를 끝낸다.
    this.settleHeldPage();
    return this.goToPage(1);
  }

  // 페이지가 행 수 선호를 소유하고 마운트마다 전달한다. 그래서 같은 크기면 다시 로드하지 않는다.
  // 이 호출은 initialize() 와 같은 시점에 오므로, 다시 로드하면 첫 페이지를 두 번 받는다.
  override setPageSize(size: number): Promise<void> {
    if (size === this.getSnapshot().size) {
      return Promise.resolve();
    }
    return super.setPageSize(size);
  }

  // 각 행의 선택 가능 여부와 함께 선택 상한을 적용한다.
  protected override canSelectMore(): boolean {
    return this.selectedRowCount < MAX_POLICY_SELECTION;
  }

  // ?results_policy 딥링크의 id 를 창이 표시할 이름으로 바꾼다. 입력 순서를 유지한다.
  //   - 카탈로그를 아직 로드하지 않았으면 null 을 반환해 대기를 알린다.
  //   - 카탈로그 밖 id 는 id 를 이름으로 대신 써서 창을 열 수 있게 한다. run 표는 서버
  //     policy_id 필터로 정확한 결과를 표시한다.
  resolvePolicyRefs(ids: readonly string[]): readonly SelectedPolicyRef[] | null {
    if (this.catalogNamesById === null) {
      return null;
    }
    const names = this.catalogNamesById;
    return ids.map((policyId) => ({ policyId, policyName: names.get(policyId) ?? policyId }));
  }

  // Evaluation Results 창의 정책 필터가 쓰는 선택 정책 스냅샷.
  getSelectedPolicies(): readonly SelectedPolicyRef[] {
    return this.getSelectedRows().map((row) => ({
      policyId: row.getRowId(),
      policyName: row.name,
    }));
  }

  // 현재 페이지만 정렬하지 않고, 캐시된 전체 결과를 정렬한 뒤 자른다.
  override get sortsOnServer(): boolean {
    return true;
  }

  // 페이지에 예산 단위가 섞이면 패널이 "#" 헤더를 접는다. 그래도 적용된 정렬은 해제할 수 있어야
  // 한다. 정렬 상태는 DataTable 이 소유하므로 패널은 적용된 열을 여기서 읽는다.
  get sortedColumnKey(): string | null {
    return this.sortState?.columnKey ?? null;
  }

  // 정렬이 바뀌면 페이지 캐시를 무효로 하고, 다시 조회하지 않고 1페이지부터 자른다.
  // DataTable 은 마운트할 때 초기 null 정렬도 알린다. 그래서 변화 없는 알림은 다시 로드하지
  // 않는다. 첫 로드는 패널의 initialize() 가 소유한다.
  override setSort(sort: DataTableSortState | null): void {
    if (sortSignature(sort) === sortSignature(this.sortState)) {
      super.setSort(sort);
      return;
    }
    super.setSort(sort);
    void this.goToPage(1);
  }

  // 검색 필터와 등록 기간은 여기에 넣고 serverSignature() 에는 넣지 않는다. 두 값은 요청은
  // 바꾸지 않고 페이지의 행만 바꾼다. 그래서 페이지 캐시는 miss 가 나고 API 캐시는 hit 한다.
  // 그 결과 필터를 바꿔도 왕복 요청이 0 번이다(rules.md §7). 기간의 기준 시각은 페이지를 만들
  // 때 정한다. 그래서 나중에 다시 방문한 캐시 페이지는 그 기준 시각을 유지한다. api 계층의
  // 카탈로그 캐시도 같은 정도로 오래된 값을 가진다.
  protected override cacheSignature(): string {
    return `${this.serverSignature()}|${this.textFilterSignature}|${this.registeredWithin}|${sortSignature(this.sortState)}`;
  }

  // 그룹 목록을 아직 받는 중인지 알린다. 그동안 null groupUuid 는 "그룹 없음" 이 아니라 "아직
  // 모름" 이다. 그래서 표는 빈 페이지를 확정하지 않고 로딩을 유지한다. 빈 페이지를 확정하면
  // 정책 없음 메시지가 잠깐 보인다.
  setGroupResolving(resolving: boolean): void {
    if (this.groupResolving === resolving) {
      return;
    }
    this.groupResolving = resolving;
    if (!resolving) {
      // 해석이 끝났다. 그룹이 없으면 대기 중인 페이지를 실제 빈 결과로 끝낸다. 그룹이 있으면
      // setGroup 이 기본 요청 serial 로 그 페이지를 대체한다.
      this.settleHeldPage();
    }
  }

  private settleHeldPage(): void {
    const settle = this.heldPage;
    this.heldPage = null;
    settle?.();
  }

  // 새로고침 버튼 전용. 페이지 캐시만 비우면 API 계층의 스코프 카탈로그 캐시가 이전 목록을 그대로
  // 반환한다. 자동 등록된 Policy 는 FE 등록 경로의 무효화를 거치지 않는다. 그래서 이 버튼만 그
  // Policy 를 불러올 수 있다. refresh() 를 재정의하지 않는 이유는 c6 initialize() 가 refresh() 를
  // 호출하기 때문이다. 마운트와 스코프 전환은 API 캐시를 재사용해야 한다.
  reload(): Promise<void> {
    invalidateScopedPolicyCatalog();
    return this.refresh();
  }

  protected override async onPage(
    req: DataTablePageReq,
  ): Promise<DataTablePageResp<PolicyTableRow>> {
    // Team 스코프는 그룹을 고르기 전에는 유효한 서버 스코프가 없다. 빈 목록을 반환하면
    // initialize() 다음 setGroup() 이 요청을 두 번 보내는 것도 막는다.
    if (this.scope === "team" && this.groupUuid === null) {
      const empty = { rows: [], total: 0, page: req.page, size: req.size };
      if (!this.groupResolving) {
        // 그룹이 없으면 URL seed 와 비교할 카탈로그도 없다. seed 를 적용할 수 없으므로 선택을
        // 복원된 것으로 표시하고, 미러가 파라미터를 지우게 한다.
        this.pendingSelectionIds = null;
        return empty;
      }
      // 그룹 목록을 받는 동안 loadState 가 "loading" 에 머물도록 페이지를 열어 둔다.
      // 대기 중인 URL seed 도 여기서 기다린다. 그룹이 해석되면 seed 를 적용할 수 있다.
      this.settleHeldPage();
      return new Promise<DataTablePageResp<PolicyTableRow>>((resolve) => {
        this.heldPage = () => resolve(empty);
      });
    }
    const policies = await this.loadScopePolicies();
    // Real-only 정책은 실행 대상이 아니다. URL 복원·정렬·자르기 전에 거른다. 그래야 숨긴 id 가
    // 선택에 남지 않고 페이지 합계가 정확하다. 엄격 비교를 쓰므로 backend 를 먼저 배포하는
    // rolling 배포 동안 이전 backend 응답도 보인다.
    const visiblePolicies: TrainingPolicyView[] = [];
    const realOnlyIds = new Set<string>();
    for (const policy of policies) {
      if (policy.trained_only_on_real_data === true) {
        realOnlyIds.add(policy.id);
      } else {
        visiblePolicies.push(policy);
      }
    }
    this.getSelectedRows()
      .filter((row) => realOnlyIds.has(row.getRowId()))
      .forEach((row) => this.setRowSelected(row, false));
    // 자르기 전에 보이는 전체 결과를 매핑하고 정렬한다. 그래야 페이지를 넘어도 순서가 맞다.
    const allRows = visiblePolicies.map((policy) => new PolicyTableRow(policy, this.onRowActivate));
    // 딥링크 이름 해석용 카탈로그 맵. 텍스트 필터 전 전체 스코프라 필터가 숨긴 행도 찾는다.
    this.catalogNamesById = new Map(visiblePolicies.map((policy) => [policy.id, policy.name]));
    // URL seed 를 페이지 조각이 아니라 전체 카탈로그에 적용한다. 그래야 다른 페이지의 id 도
    // 선택된다. setRowsSelected 는 행마다 상한을 다시 확인하고, 넘는 행은 알리지 않고 건너뛴다.
    // 이 단계는 의도적으로 검색 필터보다 먼저 실행한다. ?policy= 딥링크는 현재 필터가 그 행을
    // 숨겨도 선택을 복원해야 한다.
    this.consumePendingSelection(allRows);
    // 정렬과 자르기 전에 좁힌다. 그래야 페이저가 읽는 total 이 일치한 행 수다. 등록 기간은
    // 페이지를 만들 때 계산하는 "now - N" 기준 시각이다.
    const registeredCutoffMs = registeredWithinCutoffMs(this.registeredWithin, Date.now());
    const matchedRows = allRows.filter(
      (row) =>
        (registeredCutoffMs === null || row.isRegisteredSince(registeredCutoffMs)) &&
        row.matchesTextFilters(this.textFilters),
    );
    if (this.sortState !== null) {
      const { columnKey, direction } = this.sortState;
      const dir = direction === "asc" ? 1 : -1;
      matchedRows.sort((a, b) => dir * a.compare(b, columnKey));
    }
    const start = (req.page - 1) * req.size;
    const pageRows = matchedRows.slice(start, start + req.size);
    return { rows: pageRows, total: matchedRows.length, page: req.page, size: req.size };
  }

  private consumePendingSelection(allRows: readonly PolicyTableRow[]): void {
    if (this.pendingSelectionIds === null) {
      return;
    }
    const pendingIds = new Set(this.pendingSelectionIds);
    this.pendingSelectionIds = null;
    const matchedRows = allRows.filter((row) => pendingIds.has(row.getRowId()));
    if (matchedRows.length > 0) {
      this.setRowsSelected(matchedRows, true);
    }
  }

  private serverSignature(): string {
    return `${this.scope}|${this.groupUuid ?? ""}|${this.memberUuid}|${this.experimentId}`;
  }

  // API 계층이 스코프 카탈로그를 캐시하므로 페이지 전환은 같은 결과를 다시 쓴다.
  private loadScopePolicies(): Promise<readonly TrainingPolicyView[]> {
    return loadScopedPolicies({
      scope: this.scope,
      groupUuid: this.groupUuid ?? undefined,
      memberUuid: this.memberUuid === ALL_SESSION_FILTER ? undefined : this.memberUuid,
      filters: {
        experimentId: this.experimentId === ALL_SESSION_FILTER ? undefined : this.experimentId,
      },
    });
  }
}
